/**
 * San José City College brand colors (W54 Worker J, S3 — theme).
 *
 * Source, ONE first-party page (the catalog CSS is robots-disallowed; the SJECCD district
 * guide is the parent's brand, not the college's): sjcc.edu/marketing/brand-style-guide.aspx
 * (read 2026-10-21, committed as onboarding/sjcc/probes/brand_style_guide.html):
 *   Primary Purple   HEX #2F1A45  RGB 47, 26, 69
 *   Secondary Purple HEX #635D9B  RGB 99, 93, 155
 *   Accent Gold      HEX #FFC627  RGB 255, 198, 39
 *   White            HEX #F8F9FA
 *   "For web and digital design, always use the HEX values listed above."
 *
 * Contrast (WCAG, measured 2026-10-21 against the token surfaces in index.css — paper
 * #F5F1EB, card #FFFFFF, dark ground #161412, dark card #1F1C19):
 *   Primary Purple #2F1A45: 13.78:1 on paper, 15.5:1 on the card; white text on it 15.5:1.
 *     The `-strong` tier is the 600 step of the same hue (#261537) — SJCC publishes no
 *     second dark purple, and white text on it is 17:1+.
 *   Secondary Purple #635D9B: 5.23:1 on paper, 5.88:1 on the card (a usable text tint;
 *     it is the light-mode brand-mark front face).
 *   Accent Gold #FFC627 fails as TEXT on light surfaces (1.40 / 1.57), and `text-accent`
 *     renders at 12 px in the audit (override badge / pill / "via override" label), so
 *     4.5:1 applies. The accent TOKEN is Accent Gold's own hue and saturation darkened
 *     until it clears 4.5 on BOTH light surfaces: #8E6800 = 4.52:1 on paper, 5.08:1 on the
 *     card. On the dark surfaces it measures 3.61:1 / 3.34:1 — the 3:1 floor only: the
 *     theme system has no dark-mode accent token (doc 51 S3-6; Foothill, De Anza and SDSU
 *     record the same). The bright gold lives in the -200/-300 tints for fills.
 *
 * Brand mark (components/ui/BrandMark.tsx): light front = Secondary Purple (a published
 * brand color), two darker oblique steps; dark front = Accent Gold (11.7:1 on the dark
 * ground), two darker gold steps. The cross-theme distance floor was removed by owner
 * decision 2026-10-14 (a school's mark follows its own brand). Ramps are HSL ladders
 * pinned at the brand values (SJCC publishes no extended palette). Text-only branding,
 * no logo asset embedded (the guide's logo package is for approved uses only).
 */
import type { ThemeColors } from '../types';

export const sjccColors: ThemeColors = {
  // Primary — Primary Purple (#2F1A45 = rgb(47, 26, 69)); -strong = the 600 step #261537.
  '--color-primary':     '47 26 69',
  '--color-primary-strong': '38 21 55',
  '--color-primary-50': '245 240 249',
  '--color-primary-100': '219 203 235',
  '--color-primary-200': '188 159 219',
  '--color-primary-300': '152 107 199',
  '--color-primary-400': '77 43 113',
  '--color-primary-500': '47 26 69',
  '--color-primary-600': '38 21 55',
  '--color-primary-700': '29 16 43',
  '--color-primary-800': '21 12 31',
  '--color-primary-900': '13 7 19',

  // Accent — Accent Gold hue, light-mode TEXT tier (#8E6800 = rgb(142, 104, 0)); see header.
  '--color-accent':     '142 104 0',
  '--color-accent-50': '255 250 235',
  '--color-accent-100': '255 236 184',
  '--color-accent-200': '255 220 122',
  '--color-accent-300': '255 200 51',
  '--color-accent-400': '203 149 0',
  '--color-accent-500': '142 104 0',
  '--color-accent-600': '114 83 0',
  '--color-accent-700': '88 64 0',
  '--color-accent-800': '64 47 0',
  '--color-accent-900': '40 29 0',

  // Brand-mark faces. Light: Secondary Purple front, two darker oblique steps. Dark: Accent
  // Gold front, two darker gold steps.
  '--color-mark-front': '99 93 155',
  '--color-mark-top':   '77 72 124',
  '--color-mark-side':  '56 52 93',
  '--color-mark-front-dm': '255 198 39',
  '--color-mark-top-dm':   '224 170 20',
  '--color-mark-side-dm':  '190 140 10',
};
